import Config from "@/config/config";

/**
 * Deep link into the client app under impersonation.
 *
 * The client app's `useImpersonationDeepLink` (inspect-frontend-client,
 * `src/hooks/impersonation/use-impersonation.ts`) accepts
 * `?impersonate={clientId}&returnTo={path}` for system admins: it enters
 * impersonation, strips the two params and navigates to `returnTo`.
 * `returnTo` must be a same-origin path starting with a single "/" —
 * anything else falls back to the client dashboard — so this helper only
 * ever emits such paths.
 *
 * The client app origin comes from `Config.CLIENT_APP_URL`
 * (`REACT_APP_CLIENT_APP_URL`, derived from the admin hostname when unset).
 *
 * `DEFAULT_CLIENT_APP_RETURN_TO` is the landing page when a caller passes no
 * (or an unsafe) `returnTo` — e.g. the Tenant Setup console's "Open client
 * app as this tenant" button. Callers that know where to land (a checklist
 * just imported, a readiness-panel link) pass their own path.
 */
export const DEFAULT_CLIENT_APP_RETURN_TO = "/dashboard";

export function clientAppBaseUrl(): string {
	return (Config.CLIENT_APP_URL || "").replace(/\/+$/, "");
}

function safeReturnTo(returnTo: string | undefined): string {
	const value = returnTo?.trim();
	if (!value || !value.startsWith("/") || value.startsWith("//")) {
		return DEFAULT_CLIENT_APP_RETURN_TO;
	}
	return value;
}

export function buildClientAppImpersonationUrl(
	clientId: string,
	returnTo: string = DEFAULT_CLIENT_APP_RETURN_TO,
): string {
	const params = new URLSearchParams({
		impersonate: clientId,
		returnTo: safeReturnTo(returnTo),
	});
	return `${clientAppBaseUrl()}/?${params.toString()}`;
}

/** Opens the client app in a new tab, impersonating `clientId` and landing on `returnTo`. */
export function openClientAppAsTenant(
	clientId: string,
	returnTo?: string,
): void {
	window.open(
		buildClientAppImpersonationUrl(clientId, returnTo),
		"_blank",
		"noopener,noreferrer",
	);
}
